<template>
  <div class="tw-flex tw-gap-x-5">
    <filter-menu
      v-if="Settings.allowFiltering"
      :id="SUBSCRIBED_MODS_ID"
      :show="filterMenuOpen"
      :tag-options="game.tag_options"
      :sort-options="LIBRARY_MODS_SORT_OPTIONS"
      @update="updateTable"
    />
    <div
      v-if="subscribedModList.data.length < 1 && !loadingSubs"
      class="tw-flex-1"
    >
      <no-result
        class="tw-w-full"
        item-name="mod"
        :is-filtering="hasAnyFilters"
        @click="resetAllFilters"
      />
    </div>
    <div
      v-else
      :class="
        Settings.allowFiltering && filterMenuOpen
          ? 'md:tw-w-[calc(100%-17.25rem-1px)]'
          : 'tw-w-full'
      "
    >
      <modio-table
        ref="subscribedModsTable"
        :table-id="SUBSCRIBED_MODS_ID"
        :class="Classes.LIBRARY"
        :row-schema="schema"
        :items="subscribedModList"
        :item-total="subscribedModList.data.length"
        :fetch="false"
        :limit="50"
        :loading="loadingSubs"
        :error="errorSubs"
        :no-data-text="
          search
            ? $t('no_ugc_found', {
                ugc_name: ugcName(null, false, true).value,
              })
            : $t('no_subscriptions', {
                ugc_name: ugcName(null, false, true).value,
              })
        "
      />
    </div>
  </div>

  <base-modal
    v-if="Settings.allowFiltering && filterMenuOpen"
    show
    no-margin
    no-padding
    bottom
    has-close-btn
    :close-on-escape="false"
    class="sm:tw-hidden"
    @close:modal="toggleFilterMenu"
  >
    <filter-menu
      :id="SUBSCRIBED_MODS_ID"
      show
      modal
      :input-loading="!game?.tag_options"
      :tag-options="game?.tag_options"
      :sort-options="LIBRARY_MODS_SORT_OPTIONS"
      @update="updateTable"
    />
  </base-modal>
</template>

<script>
import { onMounted, computed, provide, watch, ref } from 'vue'
import { LIBRARY_MODS_SORT_OPTIONS } from '@config/sort.js'
import { schema } from '@config/schemas/mySubscriptions.js'
import ModioTable from '@components/Table/ModioTable.vue'
import * as sdkInterface from '@plugins/sdkInterface.js'
import FilterMenu from '@components/List/FilterMenu.vue'
import { isNullOrUndefined } from '@helpers/utils.js'
import NoResult from '@components/List/NoResult.vue'
import { Settings, GAME_ID } from '@config/game.js'
import { ITEM_TYPES } from '@config/options.js'
import Classes from '@shared/js/config/css.js'
import { useBreakpoints } from '@vueuse/core'
import { BREAKPOINTS } from '@config'
import { useI18n } from 'vue-i18n'
import useFilter, {
  normalizeModFilters,
  SUBSCRIBED_MODS_ID,
  toggleFilterMenu,
  PLATFORM_OPTION,
  filterMenuOpen,
} from '@composables/useFilter.js'
import {
  useResourceValue,
  useSearch,
  useRoute,
  useAsync,
  addToast,
  useSort,
} from '@composables'
import {
  getMySubscriptionsRequest,
  unsubscribeToModRequest,
  subscribeToModRequest,
} from '@services'
import {
  RESOURCE_ALREADY_SUBSCRIBED,
  RESOURCE_NOT_SUBSCRIBED,
} from '@shared/js/config/errors.js'
import {
  subscriptionStore,
  activationStore,
  localModStore,
  gameStore,
} from '@stores'

export default {
  components: {
    ModioTable,
    FilterMenu,
    NoResult,
  },
  setup() {
    // We maintain a separate list to the store so you can re-subscribe
    // to a mod if you unsubscribe and don't navigate away
    const { ugcName } = useResourceValue()
    const { getPublicGame } = gameStore()

    const { routeQuery } = useRoute()
    const { t } = useI18n()

    const { setSort } = useSort(
      SUBSCRIBED_MODS_ID,
      undefined,
      LIBRARY_MODS_SORT_OPTIONS
    )

    const defaultSort = LIBRARY_MODS_SORT_OPTIONS.find((x) => x.default)

    setSort({
      sortBy: defaultSort.key,
      order: defaultSort.direction,
      direction: defaultSort.direction,
    })

    const { localMods, setLocalMods } = localModStore()
    const {
      removeActivation,
      setMyActivations,
      addActivation,
      activations,
      getKey,
    } = activationStore()
    const {
      setMySubscriptions,
      removeSubscription,
      addSubscription,
      subscriptions,
    } = subscriptionStore()

    const subscribedMods = ref(
      subscriptions.value?.filter((mod) => mod.game_id === GAME_ID)
    )
    const subscribedModsTable = ref(undefined)

    const {
      clearUserSearch,
      searchUserName,
      inMemorySearch,
      clearSearch,
      search,
    } = useSearch(SUBSCRIBED_MODS_ID)

    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const smAndUp = greaterOrEqual('sm')

    const {
      activeFiltersCount,
      inMemoryFilter,
      resetFilter,
      setFilter,
      filter,
    } = useFilter(SUBSCRIBED_MODS_ID)

    const game = getPublicGame()

    const hasAnyFilters = computed(
      () =>
        !!activeFiltersCount.value || !!search.value || !!searchUserName.value
    )

    watch(smAndUp, (_smAndUp) => {
      if (Settings.value.allowFiltering && _smAndUp !== filterMenuOpen.value) {
        toggleFilterMenu()
      }
    })

    const {
      loading: loadingSubs,
      error: errorSubs,
      data: dataSubs,
      run: runSubs,
    } = useAsync(getMySubscriptionsRequest)

    const {
      run: runResub,
      error: errorResub,
      errorRef: resubErrorRef,
    } = useAsync((gameId, modId) => subscribeToModRequest({ gameId, modId }))

    const {
      run: runUnsub,
      error: errorUnsub,
      errorRef: unsubErrorRef,
    } = useAsync((gameId, modId) => unsubscribeToModRequest({ gameId, modId }))

    const localModsList = computed(() =>
      localMods.value.map((m) => {
        const missingDependencies = Array.isArray(m.dependencies)
          ? m.dependencies.filter(
              (depId) =>
                !localMods.value.some(
                  (mod) => String(mod.modId) === String(depId)
                )
            )
          : []

        return {
          id: m.modId,
          name: m.modName,
          logo: {
            thumb_320x180: m.image,
          },
          modfile: {
            filesize: m.size,
            version: m.version,
          },
          date_live: isNullOrUndefined(m.dateLive) ? '-' : m.dateLive,
          date_updated: isNullOrUndefined(m.dateUpdated) ? '-' : m.dateUpdated,
          stats: {
            downloads_total: isNullOrUndefined(m.downloads) ? '-' : m.downloads,
            subscribers_total: isNullOrUndefined(m.subscribers)
              ? '-'
              : m.subscribers,
            ratings_total: isNullOrUndefined(m.rating) ? '-' : m.rating,
          },
          disabled: !!missingDependencies.length,
          missing_dependencies: missingDependencies,
          local: true,
        }
      })
    )

    const subscribedModList = computed(() => {
      let data = [...subscribedMods.value, ...localModsList.value]
      data = inMemorySearch(data)
      data = inMemoryFilter(data)

      data = data.map((m) => ({
        ...m,
        active: activations.value?.[getKey(m.local)]?.some(
          (a) => a?.toString() === m.id?.toString()
        ),
      }))

      return { data }
    })

    function setupFilters() {
      const queryTags = [routeQuery.value.tags].filter((tag) => tag).join(',')

      if (!filter.value?.length || queryTags.length) {
        setFilter(
          normalizeModFilters(game.value.tag_options, {
            tags: queryTags,
          }),
          !queryTags
        )
      }
    }

    async function fetchSubscriptions() {
      await runSubs()

      if (!errorSubs.value) {
        subscribedMods.value = setMySubscriptions(dataSubs.value.data)
      }
    }

    async function fetchActivations() {
      const activatedModIds = await sdkInterface.getActivations()

      if (activatedModIds) {
        setMyActivations(activatedModIds, false)
      }
    }

    async function fetchLocalMods() {
      const _localMods = await sdkInterface.getLocalMods()

      if (_localMods) {
        setLocalMods(_localMods)
      }
    }

    async function fetchLocalActivations() {
      const localActivatedModIds = await sdkInterface.getLocalActivations()

      if (localActivatedModIds) {
        setMyActivations(localActivatedModIds, true)
      }
    }

    onMounted(() => {
      fetchSubscriptions()
      fetchActivations()
      fetchLocalMods()
      fetchLocalActivations()
      setupFilters()

      if (
        Settings.value.allowFiltering &&
        !smAndUp.value &&
        filterMenuOpen.value
      ) {
        toggleFilterMenu()
      }
    })

    async function toggleSubscribe(mod) {
      const reSub = !!mod.unsubscribed
      reSub ? addSubscription(mod.id, mod) : removeSubscription(mod.id)
      reSub ? addActivation(mod.id) : removeActivation(mod.id)
      sdkInterface.notifySubscriptionChange(mod.id, reSub)
      mod.local
        ? sdkInterface.notifyLocalActivationChange(mod.id, reSub)
        : sdkInterface.notifyActivationChange(mod.id, reSub)

      const updatedSubscriptions = [...subscribedMods.value]
      const index = updatedSubscriptions.findIndex((m) => m.id === mod.id)
      updatedSubscriptions[index].unsubscribed = !reSub
      subscribedMods.value = updatedSubscriptions

      reSub ? await runResub(GAME_ID, mod.id) : await runUnsub(GAME_ID, mod.id)

      if (
        reSub
          ? errorResub.value &&
            resubErrorRef.value !== RESOURCE_ALREADY_SUBSCRIBED
          : errorUnsub.value && unsubErrorRef.value !== RESOURCE_NOT_SUBSCRIBED
      ) {
        // Revert action
        reSub ? removeSubscription(mod.id) : addSubscription(mod.id, mod)
        updatedSubscriptions[index].unsubscribed = reSub
        subscribedMods.value = updatedSubscriptions

        addToast({
          title: reSub ? t('subscribe_fail') : t('unsubscribe_fail'),
          isError: true,
          text: reSub ? errorResub.value : errorUnsub.value,
        })
      }
    }
    provide('toggleSubscribe', toggleSubscribe)

    function toggleActive(mod) {
      const activate = !mod.active
      activate
        ? addActivation(mod.id, mod.local)
        : removeActivation(mod.id, mod.local)
      mod.local
        ? sdkInterface.notifyLocalActivationChange(mod.id, activate)
        : sdkInterface.notifyActivationChange(mod.id, activate)
    }
    provide('toggleActive', toggleActive)

    function resetAllFilters() {
      resetFilter()
      clearSearch()
      clearUserSearch()
    }

    function updateTable(_type, option) {
      if (_type === 'sort') {
        setSort({
          sortBy: option.key,
          order: option.direction,
          direction: option.direction,
        })

        subscribedModsTable.value?.updateTable('sort', option)
      }
    }

    watch(filter, (_filter, _oldFilter) => {
      const platformFilter = _filter.find((x) => x.id === PLATFORM_OPTION)
        ?.selected[0]

      const oldPlatformFilter = _oldFilter.find((x) => x.id === PLATFORM_OPTION)
        ?.selected[0]

      if (platformFilter !== oldPlatformFilter) {
        fetchSubscriptions()
      }
    })

    return {
      LIBRARY_MODS_SORT_OPTIONS,
      subscribedModsTable,
      SUBSCRIBED_MODS_ID,
      subscribedModList,
      toggleFilterMenu,
      resetAllFilters,
      filterMenuOpen,
      hasAnyFilters,
      updateTable,
      loadingSubs,
      ITEM_TYPES,
      errorSubs,
      Settings,
      ugcName,
      Classes,
      schema,
      search,
      game,
    }
  },
}
</script>
